/**
 * aethereum demo — the instant "I get it" walkthrough.
 *
 * Spins up a throwaway anon room against the hosted backend, mints a second agent,
 * then drives a real two-agent contract collision over MCP and narrates it:
 *   - Terminal A declares POST /api/login -> { token }
 *   - Terminal B reads the room, sees A's contract, then declares a CONFLICTING shape
 *   - Terminal A reads the room again and sees the amber collision alert
 *
 * Everything goes through the live /api/mcp JSON-RPC tools/call rail, so the demo
 * exercises the exact path real agents use. Fails soft if offline (clear message,
 * never crashes). The anon room is TTL-cleaned server-side, so each run is fine.
 */

import { homedir } from "node:os";
import { AccountClient, OfflineError } from "../hive/account-client.js";

export interface DemoOptions {
  url?: string;
  home?: string;
  env?: Record<string, string | undefined>;
  fetchImpl?: typeof fetch;
}

const DEFAULT_BASE = "https://www.aethereum.dev";

/** Call an aethereum MCP tool over the hosted JSON-RPC rail and return the first
 *  text block of the result (or "" on any soft failure). */
async function callTool(
  fetchImpl: typeof fetch,
  base: string,
  token: string,
  id: number,
  name: string,
  args: Record<string, unknown>,
): Promise<string> {
  const res = await fetchImpl(`${base}/api/mcp`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json, text/event-stream",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      jsonrpc: "2.0",
      id,
      method: "tools/call",
      params: { name: `aethereum__${name}`, arguments: args },
    }),
  });
  const raw = await res.text();
  return extractToolText(raw);
}

/**
 * v6-S17: render the collision as a two-pane panel (Terminal A | Terminal B) so
 * the "same contract, incompatible shapes" moment is visually obvious. Pure +
 * testable; the demo prints it right before the closing summary.
 */
export function renderTwoPane(aShape: string, bShape: string, colWidth = 34): string {
  const bar = "─".repeat(colWidth);
  const pad = (s: string) => (s.length > colWidth ? s.slice(0, colWidth - 1) + "…" : s.padEnd(colWidth));
  const rows = [
    ["Terminal A (you)", "Terminal B (teammate)"],
    [bar, bar],
    ["POST /api/login", "POST /api/login"],
    [aShape, bShape],
  ];
  const lines = rows.map(([l, r]) => `  ${pad(l)} │  ${r}`);
  lines.push(`  ${" ".repeat(colWidth)} │`);
  lines.push(`  ⚠ COLLISION: one contract, two incompatible shapes — caught before either shipped.`);
  return lines.join("\n");
}

/** Parse a JSON-RPC tools/call response that may arrive as plain JSON or as an SSE
 *  frame (`data: {...}`). Returns the first content text block, else "". */
export function extractToolText(raw: string): string {
  const tryParse = (s: string): string | undefined => {
    try {
      const j = JSON.parse(s) as {
        result?: { content?: Array<{ type?: string; text?: string }> };
      };
      const block = j?.result?.content?.find((c) => typeof c?.text === "string");
      return block?.text;
    } catch {
      return undefined;
    }
  };
  // Plain JSON first.
  const direct = tryParse(raw.trim());
  if (direct !== undefined) return direct;
  // SSE: scan `data:` lines.
  for (const line of raw.split("\n")) {
    const t = line.trim();
    if (t.startsWith("data:")) {
      const text = tryParse(t.slice(5).trim());
      if (text !== undefined) return text;
    }
  }
  return "";
}

function indent(text: string, pad = "      "): string {
  return text
    .split("\n")
    .map((l) => (l ? pad + l : l))
    .join("\n");
}

export async function runDemo(options: DemoOptions = {}): Promise<void> {
  const home = options.home ?? homedir();
  void home;
  const env = options.env ?? (process.env as Record<string, string | undefined>);
  const fetchImpl = options.fetchImpl ?? fetch;
  const base = (options.url ?? env["AETHEREUM_SYNC_API_URL"] ?? DEFAULT_BASE)
    .replace(/\/$/, "")
    .replace(/^https:\/\/aethereum\.dev(?=$|\/)/, "https://www.aethereum.dev");

  console.log(`
  Aethereum demo — two agents, one coordination layer, a collision caught live.
  Spinning up a throwaway room on ${base} (no account, auto-cleaned)...`);

  const client = new AccountClient(base, fetchImpl);

  let roomA;
  let tokenB = "";
  try {
    roomA = await client.createAnonRoom("terminal-a");
    if (!roomA.token) throw new Error("the server did not return a room token");
    const agentB = await client.addAgent(roomA.token, roomA.roomId, "terminal-b");
    tokenB = agentB.token;
    if (!tokenB) throw new Error("the server did not mint a second agent");
  } catch (err) {
    if (err instanceof OfflineError) {
      console.log("\n  Could not reach Aethereum (offline?). Try the demo again when you are online.\n");
    } else {
      console.log(`\n  Could not start the demo: ${err instanceof Error ? err.message : String(err)}\n`);
    }
    process.exit(1);
    return;
  }

  const tokenA = roomA.token;
  const view = roomA.viewUrl || `${base}/dashboard`;

  try {
    // ── Step 1: Terminal A shares intent + declares a contract ──────────────
    console.log(`
  Terminal A (you) is about to build the login endpoint.`);
    await callTool(fetchImpl, base, tokenA, 1, "share_intent", {
      text: "building the auth service: POST /api/login",
    });
    await callTool(fetchImpl, base, tokenA, 2, "declare_contract", {
      name: "POST /api/login",
      shape: "{ email, password } -> { token }",
    });
    console.log(`      A declares contract  POST /api/login  ->  { token }`);

    // ── Step 2: Terminal B reads the room and SEES A's contract ─────────────
    console.log(`
  Terminal B (your teammate's agent) reads the room before building...`);
    const bContext = await callTool(fetchImpl, base, tokenB, 3, "get_team_context", {});
    if (bContext) console.log(indent(bContext.split("\n").slice(0, 12).join("\n")));
    console.log(`      B can see A's contract. No code written yet, already in sync.`);

    // ── Step 3: Terminal B declares a CONFLICTING shape ─────────────────────
    console.log(`
  But B independently builds login with a DIFFERENT response shape...`);
    await callTool(fetchImpl, base, tokenB, 4, "declare_contract", {
      name: "POST /api/login",
      shape: "{ user, pass } -> { accessToken, refreshToken }",
    });
    console.log(`      B declares  POST /api/login  ->  { accessToken, refreshToken }`);

    // ── Step 4: Terminal A reads again and sees the COLLISION alert ─────────
    console.log(`
  Terminal A reads the room again...`);
    const aContext = await callTool(fetchImpl, base, tokenA, 5, "get_team_context", {});
    if (aContext) console.log(indent(aContext.split("\n").slice(0, 16).join("\n")));

    console.log("\n" + renderTwoPane("-> { token }", "-> { accessToken, refreshToken }"));

    console.log(`
  ───────────────────────────────────────────────────────────────────────────
  Collision caught. Two agents declared POST /api/login with incompatible shapes,
  and Aethereum surfaced it the moment it happened, before either shipped.

  That is the whole product: your agents coordinate before they collide.

  Watch this room:  ${view}
  Wire your own project:  aethereum init
  ───────────────────────────────────────────────────────────────────────────
`);
  } catch (err) {
    console.log(
      `\n  The demo hit a snag mid-run: ${err instanceof Error ? err.message : String(err)}.\n  Try again, or run 'aethereum init' to set up your own room.\n`,
    );
    process.exit(1);
  }
}
